import { supabase } from '@/lib/supabase';

export interface AuthUser {
  id: string;
  email: string;
  full_name?: string;
  phone?: string;
  avatar_url?: string;
  created_at: string;
}

export interface SignUpData {
  email: string;
  password: string;
  full_name: string;
  phone: string;
  // Novos campos importantes
  avatar_url?: string;
  gender?: 'masculino' | 'feminino' | 'outro';
  is_prestador?: boolean;
  endereco_completo?: string;
  endereco?: string | null;
  provincia_id?: string;
  municipio_id?: string;
  distrito_id?: string;
  latitude?: number;
  longitude?: number;
  // Novos campos para documentos (obrigatórios para prestadores)
  bi_nif?: string;
  tipo_documento?: 'BI' | 'NIF' | 'passaporte' | 'outro';
  // Campos específicos para prestadores
  categoria_id?: string;
  subcategoria_id?: string;
  tipo_servico?: 'presencial' | 'online' | 'ambos';
  anos_experiencia?: number;
  descricao_profissional?: string;
  plano_subscricao_id?: string;
  metodo_pagamento?: string; // Removido tipo específico para aceitar qualquer string
  dados_bancarios?: any; // Mudado para any para aceitar qualquer estrutura
  escritorio_localizacao?: string;
  // Campos de aceitação de termos
  acceptedTerms?: boolean;
  acceptedPrivacy?: boolean;
}

export interface SignInData {
  email: string;
  password: string;
}

export interface UpdateProfileData {
  full_name?: string;
  phone?: string;
  avatar_url?: string;
}

class AuthService {
  // Sign Up
  async signUp(data: SignUpData): Promise<{ user: AuthUser | null; error: string | null }> {
    console.log('🔐 Iniciando signUp:', { email: data.email, hasPassword: !!data.password })
    console.log('📊 Supabase configurado corretamente')
    
    try {
      console.log('✅ Prosseguindo direto para criação de conta (validação RPC desabilitada)')
      
      // Só o formato. Já não se pergunta à BD se o e-mail tem conta
      // (verificar_status_email deixava qualquer pessoa descobrir que e-mails
      // existem — auditoria 08/10, S8): um e-mail repetido é tratado mais abaixo
      // pela resposta do próprio signUp.
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.email)) {
        return { user: null, error: 'Email inválido' };
      }

      console.log('✅ Validação aprovada, criando conta...')
      
      // CONECTIVIDADE OK: Containers Supabase estão rodando
      console.log('✅ Containers Supabase verificados e funcionando')
      
      // Configuração adicional para reduzir timeouts
      const signUpOptions = {
        email: data.email,
        password: data.password,
        options: {
          // O gatilho handle_new_user (BD) cria o perfil a partir destes
          // metadados. Com a confirmação de email ligada não há sessão logo a
          // seguir ao registo, por isso o perfil completo tem de vir daqui.
          data: {
            full_name: data.full_name,
            phone: data.phone,
            gender: data.gender || null,
            endereco: data.endereco || null,
            provincia_id: data.provincia_id || null,
            municipio_id: data.municipio_id || null,
            distrito_id: data.distrito_id || null,
          },
          // Configuração para reduzir problemas de timeout
          emailRedirectTo: undefined, // Evitar redirects desnecessários
        },
      }
      
      console.log('📧 Tentando criar usuário no Supabase Auth...')
      
      // Implementar timeout mais curto: 10 segundos
      const signUpPromise = supabase.auth.signUp(signUpOptions)
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('SignUp timeout após 10 segundos')), 10000)
      )
      
      let authData, error
      
      try {
        const result = await Promise.race([signUpPromise, timeoutPromise]) as any
        authData = result.data
        error = result.error
        console.log('✅ SignUp retornou resultado')
      } catch (timeoutError) {
        console.error('💥 TIMEOUT NO AUTH.SIGNUP!')
        error = timeoutError
        authData = null
      }

      console.log('📋 Dados de autenticação:', authData)
      console.log('❌ Erro de autenticação:', error)

      if (error) {
        console.error('❌ Erro no signUp:', error)
        
        // Verificar se é timeout
        if (error.message?.includes('timeout')) {
          console.error('💥 TIMEOUT NO SUPABASE AUTH!')
          console.error('💥 Possíveis causas:')
          console.error('💥 1. Supabase Auth está sobrecarregado')
          console.error('💥 2. Problemas de rede local')
          console.error('💥 3. Container supabase_auth_clientes com problemas')
          
          // Diagnóstico mais específico para Auth
          console.log('🔍 Problema específico no serviço de autenticação:')
          console.log('🔍 - Containers estão rodando: ✅')
          console.log('🔍 - Auth endpoint trava: ❌')
          console.log('🔍 - Possível sobrecarga no supabase_auth_clientes')
          console.log('💡 Solução: Contactar administrador para reiniciar container auth')
          
          return { user: null, error: 'Serviço de autenticação temporariamente indisponível. Tente novamente em alguns minutos.' }
        }
        
        // Verificar se é erro de usuário já existente
        if (error.message === 'User already registered') {
          console.log('📧 Usuário já existe — Auth confirma e-mail registado.')

          // O signUp não devolve o id do utilizador existente (por desenho, evita
          // enumeração de e-mails), e nenhuma leitura por e-mail está aberta ao
          // browser (protege contra enumeração), por isso basta esta mensagem.
          return {
            user: null,
            error: 'E-mail já registado. Inicie sessão ou recupere a password.'
          }
        } else {
          return { user: null, error: error.message };
        }
      }

      if (authData.user) {
        // Comportamento anti-enumeração do Supabase: quando o e-mail já tem
        // conta, signUp() NÃO devolve erro — devolve sucesso com um
        // "utilizador fantasma" cujo identities vem vazio (e normalmente sem
        // sessão). Sem esta verificação, o código seguia para tentar criar o
        // perfil com este id, que ou já existe (conflito) ou não tem sessão
        // válida para passar o RLS (auth.uid() = id) — daí o "Erro ao criar
        // perfil do cliente"/"Erro ao criar conta" que os utilizadores viram
        // ao tentar registar-se com um e-mail já usado. Confirmado em
        // produção (registo de teste em tamoaki.co.ao/register).
        if (Array.isArray(authData.user.identities) && authData.user.identities.length === 0) {
          console.log('📧 Supabase devolveu utilizador "fantasma" — e-mail já registado')
          return {
            user: null,
            error: 'E-mail já registado. Inicie sessão ou recupere a password.'
          }
        }

        // Confirmação de email ligada (01/10/2026): o signUp devolve o
        // utilizador SEM sessão até o email ser confirmado. Sem sessão o
        // upsert em profiles é recusado pelas políticas RLS (só
        // authenticated) e o cliente via "Erro ao criar perfil do cliente"
        // com a conta já criada. O perfil completo já foi gravado pelo
        // gatilho handle_new_user a partir dos metadados acima.
        if (!authData.session) {
          return {
            user: {
              id: authData.user.id,
              email: authData.user.email!,
              full_name: data.full_name,
              phone: data.phone,
              created_at: authData.user.created_at,
            },
            error: null,
          };
        }

        console.log('✅ Usuário criado, criando perfil...')
        // Registos em tamoaki.co.ao são SEMPRE de clientes ('profiles').
        // O auto-registo de prestadores vive em prestador.tamoaki.co.ao
        // (RPC prestadores_create, que exige aprovação do backoffice).
        const profileData = {
            id: authData.user.id,
            full_name: data.full_name,
            phone: data.phone,
            email: data.email,
            avatar_url: data.avatar_url || null,
            gender: data.gender || 'outro', // Campo obrigatório
            endereco_completo: data.endereco || '', // Campo obrigatório
            // Sem zona escolhida fica vazio. Os antigos "padrões" (Luanda /
            // Maianga 550e8400-…) eram ids que não existem nas tabelas —
            // 13 perfis ficaram com zona inválida (achado 2026-09-28).
            provincia_id: data.provincia_id || null,
            municipio_id: data.municipio_id || null,
            distrito_id: data.distrito_id || null,
            // Removidos: latitude, longitude (não existem na tabela profiles)
            // 26/10/2026: era "|| true" (gravava sempre sim). A prova da
            // aceitação, com a versão, fica em consentimentos — registada no
            // primeiro acesso com sessão (lib/consentimentoPoliticas.ts).
            aceitar_termos: data.acceptedTerms === true,
            aceitar_privacidade: data.acceptedPrivacy === true,
            // Campos essenciais para clientes:
            nacionalidade: 'Angolana', // Campo obrigatório
            status: 'ativo', // Campo obrigatório
            verificado: false, // Campo obrigatório
            idioma_preferido: 'pt', // Campo obrigatório
            receber_notificacoes: true // Campo obrigatório
            // Removidos: is_prestador (não existe na tabela), bi_nif, tipo_documento, documento_verificado, categoria, flagged, codigo_referencia, referido_por
          };

          console.log('📋 Dados do perfil (cliente) para inserção:', JSON.stringify(profileData, null, 2));

          // CAUSA RAIZ REAL (confirmada em produção com testes ponta-a-ponta,
          // 31/07/2026): existe um trigger no servidor — on_auth_user_created
          // AFTER INSERT ON auth.users EXECUTE FUNCTION handle_new_user() —
          // que já cria automaticamente uma linha mínima em profiles (sem
          // email, sem endereço, gender genérico) no preciso instante do
          // signUp, ANTES deste código client-side sequer correr. Isto é
          // determinístico, não uma corrida ocasional (confirmado via
          // pedidos diretos ao PostgREST). Por isso o antigo .insert() ia
          // SEMPRE bater num 23505 (duplicate key em profiles_pkey) — a
          // conta era sempre criada, mas com dados mínimos, e o utilizador
          // via sempre "Erro ao criar perfil do cliente" mesmo assim. Isto
          // explica a estatística real na BD: 19 de 22 clientes registados
          // tinham profiles.email nulo.
          //
          // Fix: usar upsert (ON CONFLICT DO UPDATE) em vez de insert — a
          // linha do trigger já existe, por isso isto atualiza-a com todos
          // os dados reais do formulário (incluindo email) em vez de tentar
          // criar uma segunda linha e falhar.
          //
          // Sem .select() de propósito: se a sessão ainda não tiver
          // propagado (role "anon"), esse role tem privilégio de escrita em
          // profiles.email mas NÃO de leitura (coluna trancada por
          // segurança — só "authenticated"/"service_role" podem ler
          // e-mails); pedir .select() a seguir negaria o pedido inteiro por
          // essa coluna, mesmo com a escrita já bem-sucedida.
          console.log('⏳ A garantir perfil completo para o novo utilizador (upsert)...')
          console.log('🔐 Usuário criado com ID:', authData.user.id)

          const { error: profileError } = await supabase
            .from('profiles')
            .upsert([profileData], { onConflict: 'id' })

          console.log('❌ Erro do perfil:', profileError)

          if (profileError) {
            console.error('❌ Error creating profile:', profileError);
            
            // Se ainda der erro 401, tentar uma abordagem alternativa
            if (profileError.code === 'PGRST301' || profileError.message?.includes('401')) {
              console.log('🔄 Tentando abordagem alternativa: inserir sem RLS...')
              
              // Tentar usar uma função RPC que bypassa RLS
              const { data: rpcResult, error: rpcError } = await supabase.rpc('create_user_profile', {
                user_id: authData.user.id,
                profile_data: profileData
              })
              
              if (rpcError) {
                console.error('❌ RPC também falhou:', rpcError)
                return { user: null, error: 'Erro ao criar perfil do cliente. Tente fazer login novamente.' };
              }
              
              console.log('✅ Perfil criado via RPC:', rpcResult)
            } else {
              return { user: null, error: 'Erro ao criar perfil do cliente' };
            }
          }

          console.log('✅ Cliente criado com sucesso na tabela profiles!')
          return { user: authData.user, error: null };

        console.log('🎉 Registro concluído com sucesso!')
        return {
          user: {
            id: authData.user.id,
            email: authData.user.email!,
            full_name: data.full_name,
            phone: data.phone,
            created_at: authData.user.created_at,
          },
          error: null,
        };
      }

      console.log('❌ Nenhum usuário retornado do signUp')
      return { user: null, error: 'Erro ao criar conta' };
    } catch (error) {
      console.error('💥 Erro inesperado no signUp:', error)
      return { user: null, error: 'Erro inesperado durante o registro' };
    }
  }

  // Sign In
  async signIn(data: SignInData): Promise<{ user: AuthUser | null; error: string | null }> {
    try {
      console.log('🔐 Iniciando login para:', data.email)
      console.log('✅ Prosseguindo com login direto (validação RPC desabilitada)')

      // Login com timeout e telemetria simples
      console.log('⏱️ Aguardando resposta do Supabase...')

      const startTime = Date.now()
      const signInPromise = supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      })
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('SignIn timeout após 10 segundos')), 10000)
      )

      let result: any
      try {
        result = await Promise.race([signInPromise, timeoutPromise])
      } catch (e) {
        console.error('💥 TIMEOUT NO AUTH.SIGNIN!', e)
        return { user: null, error: 'Serviço de autenticação indisponível. Tente novamente em instantes.' }
      }

      const endTime = Date.now()
      const authData = result.data
      const error = result.error
      console.log('⏱️ Login levou:', endTime - startTime, 'ms')
      console.log('✅ SignIn retornou resultado:', { hasUser: !!authData?.user, hasError: !!error })

      if (error) {
        console.error('❌ Erro no login:', error)
        return { user: null, error: error.message };
      }

      if (authData.user) {
        // Garantir que a sessão esteja estabelecida para atender RLS imediatamente
        const pollStart = Date.now()
        let sessionOk = false
        while (!sessionOk && Date.now() - pollStart < 5000) {
          const { data: { session } } = await supabase.auth.getSession()
          sessionOk = !!session
          if (!sessionOk) await new Promise(r => setTimeout(r, 250))
        }

        // CAUSA RAIZ do "fica sempre a pedir login": mesmo quando a sessão
        // nunca chegava a ficar legível (ex.: rede lenta, cookies de
        // terceiros bloqueados), esta função devolvia sucesso na mesma —
        // Login.tsx navegava para /dashboard com um "user" fabricado que a
        // AuthProvider aceitava sem questionar. Ao recarregar/navegar
        // depois, AuthProvider.getSession() já não encontrava sessão
        // nenhuma, user voltava a null, e ProtectedRoute mandava de volta
        // para /login — um ciclo que parecia "nunca deixa entrar", apesar
        // do login em si ter "funcionado". Sem sessão real confirmada, é
        // mais correto devolver erro do que fingir sucesso.
        if (!sessionOk) {
          console.error('❌ Sessão não ficou legível após login (5s) — a devolver erro em vez de sucesso falso')
          return {
            user: null,
            error: 'Não foi possível confirmar a sessão. Verifique a sua ligação à internet e tente novamente.',
          }
        }

        console.log('✅ Login realizado com sucesso!')
        return {
          user: {
            id: authData.user.id,
            email: authData.user.email!,
            full_name: authData.user.user_metadata?.full_name || 'Usuário',
            phone: authData.user.user_metadata?.phone || '',
            avatar_url: authData.user.user_metadata?.avatar_url || null,
            created_at: authData.user.created_at,
          },
          error: null,
        };
      }

      return { user: null, error: 'Erro ao fazer login' };
    } catch (error) {
      console.error('💥 Erro inesperado no login:', error)
      return { user: null, error: error.message || 'Erro inesperado' };
    }
  }

  // Sign Out
  async signOut(): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.auth.signOut();
      return { error: error?.message || null };
    } catch (error) {
      return { error: error.message || 'Erro inesperado' };
    }
  }

  // OAuth - Google
  async signInWithGoogle(): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + '/oauth/callback',
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      })
      return { error: error?.message || null }
    } catch (e) {
      return { error: e.message || 'Erro inesperado ao iniciar OAuth Google' }
    }
  }

  // OAuth - Facebook
  async signInWithFacebook(): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options: {
          redirectTo: window.location.origin + '/oauth/callback',
        }
      })
      return { error: error?.message || null }
    } catch (e) {
      return { error: e.message || 'Erro inesperado ao iniciar OAuth Facebook' }
    }
  }

  // Get Current User
  async getCurrentUser(): Promise<{ user: AuthUser | null; error: string | null }> {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();

      if (error || !user) {
        return { user: null, error: error?.message || 'Usuário não autenticado' };
      }

      // Get profile data
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) {
          console.error('Error fetching profile:', profileError);
        }

      return {
        user: {
          id: user.id,
          email: user.email!,
          full_name: profile?.full_name || user.user_metadata?.full_name,
          phone: profile?.phone || user.user_metadata?.phone,
          avatar_url: profile?.avatar_url,
          created_at: user.created_at,
        },
        error: null,
      };
    } catch (error) {
      return { user: null, error: (error as Error).message || 'Erro inesperado' };
    }
  }

  // Update Profile
  async updateProfile(userId: string, data: UpdateProfileData): Promise<{ user: AuthUser | null; error: string | null }> {
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .update(data)
        .eq('id', userId)
        .select()
        .single();

      if (error) {
        return { user: null, error: error.message };
      }

      // Update auth metadata
      const { error: authError } = await supabase.auth.updateUser({
        data: {
          full_name: data.full_name,
          phone: data.phone,
        },
      });

      if (authError) {
        console.error('Error updating auth metadata:', authError);
      }

      // Get current user to get email
      const { data: { user } } = await supabase.auth.getUser();

      return {
        user: {
          id: profile.id,
          email: user?.email || '',
          full_name: profile.full_name,
          phone: profile.phone,
          avatar_url: profile.avatar_url,
          created_at: profile.created_at,
        },
        error: null,
      };
    } catch (error) {
      return { user: null, error: (error as Error).message || 'Erro inesperado' };
    }
  }

  // Reset Password
  async resetPassword(email: string): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      return { error: error?.message || null };
    } catch (error) {
      return { error: (error as Error).message || 'Erro inesperado' };
    }
  }

  // Change Password
  async changePassword(newPassword: string): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      return { error: error?.message || null };
    } catch (error) {
      return { error: (error as Error).message || 'Erro inesperado' };
    }
  }

  // Listen to auth changes
  onAuthStateChange(callback: (user: AuthUser | null) => void) {
    return supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        // Get profile data
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();

        const user: AuthUser = {
          id: session.user.id,
          email: session.user.email!,
          full_name: profile?.full_name || session.user.user_metadata?.full_name,
          phone: profile?.phone || session.user.user_metadata?.phone,
          avatar_url: profile?.avatar_url,
          created_at: session.user.created_at,
        };

        callback(user);
      } else if (event === 'SIGNED_OUT') {
        callback(null);
      }
    });
  }
}

export const authService = new AuthService(); 